<template>
    <ModalContent
        flat-chrome
        :help-content-id="`popup-deliverysite-${current_entity_profile}`"
    >
        <template slot="title">{{ title }}</template>
        <template slot="content" v-if="deliverysite">
            <div class="stitch-modal general-settings-reference stitch-form">
                <Warning :elevation="2" v-if="deliverysite.customer_data?.external && isNew">
                    {{ $translate("external_customer_deliverysite_instructions") }}
                </Warning>

                <v-alert
                    outlined
                    border="left"
                    type="warning"
                    dense
                    text
                    v-if="deliverysite.entity_data && deliverysite.entity_data.archived"
                >
                    {{ $translate("customerArchivedCannotModify") }}
                </v-alert>
                <v-alert
                    outlined
                    border="left"
                    type="warning"
                    dense
                    text
                    v-if="readonly"
                >
                    <template
                        v-if="
                            deliverysite.entity && deliverysite.entity !== current_entity_id
                        "
                    >
                        {{ $translate("readonly_other_entity") }}
                    </template>
                    <template
                        v-else-if="
                            deliverysite.customer &&
                            deliverysite.customer_data.owner !== current_entity_id
                        "
                    >
                        {{ $translate("readonly_customer") }}
                    </template>
                    <template v-else-if="deliverysite?.stock">
                        {{ $translate("readonly_stock") }}
                    </template>
                    <template v-else-if="deliverysite?.archived">
                        {{ $translate("readonly_archived") }}
                    </template>
                </v-alert>

                <v-card class="stitch-modal__card" elevation="0">
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-information-outline</v-icon>
                            <span>{{ $translate("information") }}</span>
                        </h5>
                        <v-row dense class="stitch-modal__fields">
                            <v-col cols="12">
                                <v-text-field
                                    v-model="deliverysite.name"
                                    autofocus
                                    :error-messages="deliverysite_errors.name"
                                    :label="$translate('addressNameRequired')"
                                    :placeholder="$translate('addressNamePlaceholder')"
                                    :disabled="readonly"
                                    dense
                                    outlined
                                    hide-details="auto"
                                />
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="deliverysite.instructions"
                                    :error-messages="deliverysite_errors.instructions"
                                    :label="$translate('instructions')"
                                    :placeholder="$translate('instructionsPlaceholder')"
                                    name="instruction"
                                    rows="3"
                                    :disabled="readonly"
                                    dense
                                    outlined
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card class="stitch-modal__card" elevation="0">
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-map-marker-outline</v-icon>
                            <span>{{ $translate("address") }}</span>
                        </h5>
                        <AddressForm
                            v-model="deliverysite.address"
                            :title="''"
                            :errors="deliverysite_errors.address"
                            :readonly="readonly"
                            outlined
                        />
                    </v-card-text>
                </v-card>

                <v-card class="stitch-modal__card" elevation="0">
                    <v-card-text>
                        <h5 class="stitch-modal__title">
                            <v-icon size="16">mdi-clock-outline</v-icon>
                            <span>{{ $translate("deliveryHours") }}</span>
                        </h5>
                        <DeliveryHoursEditor
                            ref="hoursEditor"
                            v-model="deliverysite.delivery_hours"
                            :readonly="readonly"
                            hide-title
                        />
                    </v-card-text>
                </v-card>
            </div>
        </template>
        <template slot="bottom">
            <template v-if="!readonly">
                <v-btn
                    text
                    class="my-2 my-sm-4 w-100 w-sm-auto text-none"
                    @click="hideModal"
                >
                    {{ $translate("cancel") }}
                </v-btn>
                <v-btn
                    color="primary"
                    class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3 text-none"
                    :loading="deliverysite_saving"
                    :disabled="deliverysite_saving"
                    @click="saveInformationClicked"
                >
                    {{ save_text }}
                </v-btn>
            </template>
            <template v-else>
                <v-btn
                    text
                    class="my-2 my-sm-4 w-100 w-sm-auto text-none"
                    @click="hideModal"
                >
                    {{ $translate("close") }}
                </v-btn>
            </template>
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import GlobalMixins from "@/mixins/global";

import DeliveryHoursEditor from "@/components/deliverysite/hours-editor.vue";

export default {
    components: { DeliveryHoursEditor },
    mixins: [GlobalMixins],
    computed: {
        ...mapState("editdeliverysite", [
            "deliverysite",
            "deliverysite_saving",
            "deliverysite_errors",
        ]),
        readonly() {
            if (!this.deliverysite) {
                return true;
            }
            return !!(
                this.deliverysite.stock ||
                this.deliverysite.archived ||
                (this.deliverysite.entity &&
                    this.deliverysite.entity !== this.current_entity_id) ||
                (this.deliverysite.customer_data?.owner &&
                    this.deliverysite.customer_data.owner !== this.current_entity_id)
            );
        },
        typesChoices() {
            return [
                { text: this.$translate("company"), value: "company" },
                { text: this.$translate("individual"), value: "individual" },
            ];
        },
        isNew() {
            return !this.deliverysite.id;
        },
        save_text() {
            if (!this.isNew) {
                return this.$translate("saveInformations");
            }
            return this.$translate("createSite");
        },
        title() {
            return this.params.deliverysite.id
                ? this.$translate("editDeliveryAddress")
                : this.$translate("newDeliverySite");
        },
    },
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    async mounted() {
        await this.init({ deliverysite: this.params.deliverysite });
    },
    methods: {
        ...mapMutations("editdeliverysite", ["updateDeliverySite"]),
        ...mapActions("editdeliverysite", ["saveDeliverySite", "init"]),
        failedSavingInformations(error) {
            this.$swal({
                title: this.$translate("failedSavingDeliverySite"),
                html: this.$utils.resultToSwalContent(this, error),
                type: "error",
            });
        },
        informationsSaved(response) {
            if (response.status == 201) {
                this.addAlert(this.$translate("deliverySiteCreated"));
            }
            this.hideModal({ result: response.data });
        },
        saveInformationClicked() {
            if (!this.$refs.hoursEditor.validate()) return;
            window.setTimeout(async () => {
                try {
                    const result = await this.saveDeliverySite({ instance: this.deliverysite });
                    this.informationsSaved(result);
                } catch (error) {
                    this.failedSavingInformations(error);
                }
            });
        },
    },
    i18n: {
        messages: {
            en: {
                information: "Information",
                deliveryHours: "Delivery hours",
                customerArchivedCannotModify: "Customer has been archived, this delivery site cannot be modified",
                addressNameRequired: "Address name (*)",
                address: "Address",
                cancel: "Cancel",
                close: "Close",
                company: "Company",
                individual: "Individual",
                saveInformations: "Save information",
                createSite: "Create site",
                editDeliveryAddress: "Edit delivery address",
                newDeliverySite: "New delivery site",
                failedSavingDeliverySite: "Failed saving delivery site",
                deliverySiteCreated: "Delivery site created",
                readonly_stock:
                    "This delivery site is linked to a warehouse, information cannot be updated. Please update the warehouse instead.",
                readonly_archived:
                    "This delivery site cannot be modified because it has been archived.",
                readonly_customer:
                    "This delivery site cannot be modified because it is managed by your customer. Contact him to make changes.",
                readonly_other_entity:
                    "This delivery site cannot be modified because it is managed by your customer. Contact him to make changes.",
                external_customer_deliverysite_instructions:
                    "The customer manages their delivery sites. You should avoid creating a delivery site yourself and instead ask the customer to create it on their end.",
                instructions: "Delivery instructions",
                addressNamePlaceholder: "Main office, warehouse, etc.",
                instructionsPlaceholder: "Instructions to pass to the delivery person",
            },
            fr: {
                information: "Information",
                deliveryHours: "Horaires de livraison",
                customerArchivedCannotModify: "Le client a été archivé, cette adresse de livraison ne peut pas être modifiée",
                addressNameRequired: "Nom de l'adresse (*)",
                address: "Adresse",
                cancel: "Annuler",
                close: "Fermer",
                company: "Entreprise",
                individual: "Particulier",
                saveInformations: "Sauvegarder les informations",
                createSite: "Créer un site",
                editDeliveryAddress: "Modifier l'adresse de livraison",
                newDeliverySite: "Nouvelle adresse de livraison",
                failedSavingDeliverySite: "Erreur lors de la sauvegarde de l'adresse de livraison",
                deliverySiteCreated: "Adresse de livraison créée",
                readonly_stock:
                    "Cette adresse de livraison est liée à un entrepôt, les informations ne peuvent pas être modifiées directement. Veuillez modifier l'entrepôt à la place.",
                readonly_archived:
                    "Cette adresse de livraison ne peut pas être modifiée car elle a été archivée.",
                readonly_customer:
                    "Cette adresse de livraison ne peut pas être modifiée car elle n'est pas gérée par votre entreprise.",
                readonly_other_entity:
                    "Cette adresse de livraison ne peut pas être modifiée car elle ne vous appartient pas. Contactez son propriétaire pour effectuer des modifications.",
                external_customer_deliverysite_instructions:
                    "Le client gère ses adresses de livraison. Vous devriez éviter de créer une adresse de livraison vous-même et demander au client de la créer de son côté.",
                instructions: "Instructions de livraison",
                addressNamePlaceholder: "Bureau principal, entrepôt, etc.",
                instructionsPlaceholder: "Instructions à transmettre au livreur",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/_stitch-form.scss";
</style>
